import type { JsonApiRelationships } from 'json-api-models';

type RelatedModel = { relationships: JsonApiRelationships };

/**
 * Check whether a model's relationships are loaded along every include path.
 *
 * A relationship counts as loaded when the store has its linkage and each
 * linked model, as it does after a request that included it. Linkage alone,
 * which some relationships return by default, doesn't count.
 */
export function hasIncluded(model: RelatedModel, paths: string[]): boolean {
    return paths.every((path) => isLoaded(model, path.split('.')));
}

function isLoaded(model: RelatedModel, [name, ...rest]: string[]): boolean {
    const data = model.relationships[name]?.data;

    if (data === undefined) {
        return false;
    }

    // An empty to-one relationship is loaded, with nothing further to load.
    if (data === null) {
        return true;
    }

    // The relationship getter resolves linkage through the model's store,
    // giving null for linked models that haven't been loaded.
    const related = [
        Reflect.get(model, name) as RelatedModel | (RelatedModel | null)[] | null,
    ].flat();

    return related.every(
        (related) => related !== null && (!rest.length || isLoaded(related, rest)),
    );
}
